'use client'

import { useEffect, useRef, useState } from 'react'

import { useTranslation } from '@/hooks/client'
import { NEWSLETTER_SOURCES } from '@/lib/newsletter/sources'
import { tw } from '@/lib/utils'

import { FloatField } from './float-field'
import { SOUND_BARS } from './sound-bars'

const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

const SpinnerIcon = () => (
  <svg className="h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
)

const ArrowGlyph = () => (
  <svg
    viewBox="0 0 16 16"
    className="h-4 w-4"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 8h10M9 4l4 4-4 4" />
  </svg>
)

const CheckGlyph = () => (
  <svg
    aria-hidden
    viewBox="0 0 16 16"
    className="pointer-events-none absolute h-3 w-3 text-white opacity-0 peer-checked:opacity-100"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 8.5L6.5 12 13 4.5" />
  </svg>
)

interface FormCardProps {
  onSuccess: (subscribedMessage: string) => void
}

export const FormCard = ({ onSuccess }: FormCardProps) => {
  const { t } = useTranslation()
  const [submitMessage, setSubmitMessage] = useState('')
  const [email, setEmail] = useState({ address: '', error: false, touched: false })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const formLoadTimeRef = useRef<number | null>(null)
  const [messageActive, setMessageActive] = useState(false)
  const [focusedField, setFocusedField] = useState<string | null>(null)
  const messageRef = useRef<HTMLTextAreaElement | null>(null)

  useEffect(() => {
    formLoadTimeRef.current = Date.now()
  }, [])

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setEmail({ address: value, error: email.touched && !validateEmail(value), touched: email.touched })
  }

  const handleEmailBlur = () => {
    setEmail({ address: email.address, error: !validateEmail(email.address), touched: true })
    setFocusedField(null)
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const formData = new FormData(event.currentTarget)
    const data = Object.fromEntries(formData.entries())
    const subscribe = (document.getElementById('contact-form-subscribe') as HTMLInputElement)?.checked

    if (!validateEmail(email.address) || !email.address) {
      setEmail({ address: email.address, error: true, touched: true })
      return
    }

    setIsSubmitting(true)
    setSubmitMessage('')

    try {
      const now = Date.now()
      const timeElapsed = now - (formLoadTimeRef.current ?? now)
      const contactResponse = await fetch('/api/contact-submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          message: data.message,
          website: data.website,
          timeElapsed,
        }),
      })

      const contactResult = await contactResponse.json()
      if (contactResult.error) {
        setSubmitMessage(contactResult.message)
        setIsSubmitting(false)
        return
      }

      if (subscribe) {
        await fetch('/api/newsletter-subscribe', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: data.email,
            name: [data.firstName, data.lastName].filter(Boolean).join(' ') || null,
            userType: 'contact-form-subscriber',
            website: data.website,
            timeElapsed,
            source: NEWSLETTER_SOURCES.contactForm,
          }),
        }).catch(() => {})
      }

      onSuccess(
        subscribe
          ? t('contact.thankYouSubscribed', 'Thank you for your feedback. You are now subscribed to our newsletter.')
          : t('contact.thankYou', 'Thank you for your feedback!')
      )
    } catch {
      setSubmitMessage(t('contact.errorMessage', 'An unexpected error occurred. Please try again.'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const fieldClass = (field: string, hasError = false) =>
    tw(
      'text-navy peer w-full bg-transparent px-0 pt-7 pb-3 text-[17px] leading-[1.4] tracking-[-0.01em]',
      'border-b transition-colors duration-300 outline-none placeholder-transparent',
      hasError ? 'border-error' : focusedField === field ? 'border-navy' : 'border-navy/15 hover:border-navy/40'
    )

  const labelClass = (field: string, hasValue: boolean) =>
    tw(
      'text-navy/55 pointer-events-none absolute left-0 origin-left transition-all duration-300',
      'text-[13px] font-medium tracking-[0.18em] uppercase',
      hasValue || focusedField === field
        ? 'top-0 scale-[0.92]'
        : 'text-navy/45 top-7 scale-100 text-[15px] font-normal tracking-[-0.01em] normal-case'
    )

  return (
    <form onSubmit={handleSubmit} className="relative">
      <div
        className={tw(
          'ring-navy/10 relative rounded-[28px] bg-white p-6 ring-1 sm:p-8 md:p-10',
          'shadow-[0_30px_80px_-30px_rgba(20,37,70,0.25),0_8px_24px_-12px_rgba(20,37,70,0.08)]'
        )}
      >
        <span
          aria-hidden
          className="border-navy/30 pointer-events-none absolute top-4 left-4 h-3 w-3 border-t border-l"
        />
        <span
          aria-hidden
          className="border-navy/30 pointer-events-none absolute top-4 right-4 h-3 w-3 border-t border-r"
        />
        <span
          aria-hidden
          className="border-navy/30 pointer-events-none absolute bottom-4 left-4 h-3 w-3 border-b border-l"
        />
        <span
          aria-hidden
          className="border-navy/30 pointer-events-none absolute right-4 bottom-4 h-3 w-3 border-r border-b"
        />

        {submitMessage && (
          <div className="border-error/30 bg-error/5 text-error mb-5 rounded-lg border px-4 py-3 text-[14px]">
            {submitMessage}
          </div>
        )}

        <div className="grid grid-cols-1 gap-7 sm:grid-cols-2 sm:gap-6">
          <FloatField
            id="firstName"
            name="firstName"
            label={t('contact.firstName', 'First name')}
            type="text"
            onFocus={() => setFocusedField('firstName')}
            onBlur={() => setFocusedField(null)}
            focusedField={focusedField}
          />
          <FloatField
            id="lastName"
            name="lastName"
            label={t('contact.lastName', 'Last name')}
            type="text"
            onFocus={() => setFocusedField('lastName')}
            onBlur={() => setFocusedField(null)}
            focusedField={focusedField}
          />
        </div>

        <div className="relative mt-7">
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            value={email.address}
            onChange={handleEmailChange}
            onBlur={handleEmailBlur}
            onFocus={() => setFocusedField('email')}
            disabled={isSubmitting}
            className={fieldClass('email', email.error && email.touched)}
            placeholder=" "
          />
          <label htmlFor="email" className={labelClass('email', !!email.address)}>
            {t('subscription.email', 'Email')}
          </label>
          {email.error && email.touched && (
            <span className="text-error mt-2 block text-[12px]">
              {t('contact.invalidEmail', 'Please enter a valid email address.')}
            </span>
          )}
        </div>

        <div className="relative mt-7">
          <textarea
            id="message"
            name="message"
            ref={messageRef}
            onFocus={() => {
              setFocusedField('message')
              setMessageActive(true)
            }}
            onBlur={() => {
              setFocusedField(null)
              setMessageActive((messageRef.current?.value.length ?? 0) > 0)
            }}
            onChange={(e) => setMessageActive(e.target.value.length > 0)}
            rows={4}
            className={tw(
              'text-navy peer w-full resize-none bg-transparent px-0 pt-7 pb-3 text-[17px] leading-[1.55] tracking-[-0.01em]',
              'border-b transition-colors duration-300 outline-none placeholder-transparent',
              focusedField === 'message' ? 'border-navy' : 'border-navy/15 hover:border-navy/40'
            )}
            placeholder=" "
          />
          <label htmlFor="message" className={labelClass('message', messageActive)}>
            {t('contact.message', 'Message')}
          </label>
          <div aria-hidden className="pointer-events-none absolute right-0 bottom-3 flex h-5 items-end gap-[3px]">
            {SOUND_BARS.map((h, i) => (
              <span
                key={i}
                className={tw(
                  'block w-[2px] rounded-full transition-colors duration-300',
                  messageActive ? 'bg-lapis' : 'bg-navy/20'
                )}
                style={{
                  height: `${messageActive ? h : Math.max(15, h * 0.35)}%`,
                  animation: messageActive ? `soundBar 1.${(i % 6) + 2}s ease-in-out ${i * 0.07}s infinite` : 'none',
                }}
              />
            ))}
          </div>
        </div>

        {/* Honeypot */}
        <div className="pointer-events-none absolute left-[-9999px] opacity-0" aria-hidden="true">
          <label htmlFor="website">Website (leave blank)</label>
          <input type="text" id="website" name="website" tabIndex={-1} autoComplete="off" />
        </div>

        <label className="mt-8 flex cursor-pointer items-start gap-3">
          <span className="relative mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center">
            <input
              type="checkbox"
              id="contact-form-subscribe"
              className="border-navy/25 checked:border-navy checked:bg-navy peer h-5 w-5 appearance-none rounded-[5px] border bg-white transition-colors"
            />
            <CheckGlyph />
          </span>
          <span className="text-navy/75 text-[14px] leading-[1.5]">
            {t(
              'contact.subscribeNewsletter',
              "I want to subscribe to HearingTracker's newsletter to stay updated on the latest news, expert reviews, and insights."
            )}
          </span>
        </label>

        <button
          type="submit"
          data-tracking="ignore"
          disabled={isSubmitting}
          className={tw(
            'group bg-navy relative mt-8 flex w-full items-center justify-between gap-4 overflow-hidden rounded-2xl px-6 py-5 text-white',
            'transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_20px_40px_-12px_rgba(20,37,70,0.45)]',
            'focus-visible:ring-lapis focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
            isSubmitting && 'cursor-not-allowed opacity-70 hover:translate-y-0 hover:shadow-none'
          )}
        >
          <span
            aria-hidden
            className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/10 to-transparent transition-transform duration-700 group-hover:translate-x-full"
          />
          <span className="relative font-mono text-[11px] tracking-[0.28em] text-white/55 uppercase">
            {t('contact.sendLabel', 'Send')}
          </span>
          <span className="relative flex items-center gap-4">
            <span
              className="text-[22px] tracking-[-0.01em]"
              style={{ fontFamily: 'var(--font-fraunces), Georgia, serif', fontWeight: 500 }}
            >
              {isSubmitting
                ? t('contact.submitting', 'Sending your message…')
                : t('contact.dropLine', 'Drop us a line')}
            </span>
            <span className="text-navy flex h-10 w-10 items-center justify-center rounded-full bg-white transition-transform duration-300 group-hover:translate-x-1">
              {isSubmitting ? <SpinnerIcon /> : <ArrowGlyph />}
            </span>
          </span>
        </button>
      </div>
    </form>
  )
}
